import type {
  FieldTypeCode,
  IColumnConstraint,
  IGridColumn,
  ISnapshotConstraint
} from '../api/contracts';

/**
 * How to *ask* for a well-formed value. Not how to judge one.
 *
 * survey-api decides whether a value is acceptable and says so as an `engine.format`
 * finding, with every evaluation pass, like every other finding — and it deliberately
 * stores the value first, so nothing a respondent typed is ever lost to a check. This
 * module is the other half of that: the shape of the control that makes a well-formed
 * value easy to produce in the first place, read from the constraint the server ships
 * rather than from anything decided here.
 *
 * **There is no regex on this side, and there must not be.** The named formats
 * (`email`, `corporate_email`, `us_phone`, `postal_code_na`, `us_state`, `url`) are
 * implemented in code on the server precisely because the .NET and JavaScript regex
 * dialects diverge on named groups, `\d` under Unicode and anchoring — a pattern
 * travelling as data would let the two sides disagree silently, and a client-side
 * `pattern` attribute would be exactly that disagreement with a browser's dialect added.
 * What travels instead is `mask`, a positional spelling, and `messageCode`, a key for
 * the copy below. SR-416 added a seventh, `month_day` (`MM/DD`, masked `99/99` server-side), and a
 * date's latest day, `maxDate`, which the date input takes as its `max`. A closed set is asked as
 * a list rather than a box: a `month_day` as a month and a day (`isMonthDay`), a `year` with both
 * bounds as its years (`yearChoices`).
 *
 * The one merge this file does perform is the grid / multi-year **column** default,
 * and it is a merge the server declares is the client's: a scalar usage's constraint
 * arrives already resolved (SR-122), a column's arrives as authored, because there the
 * row is the axis and the column is what has a type (field-constraints design §11).
 * `COLUMN_DEFAULTS` below therefore mirrors `FieldConstraintDefaults.ByColumnInput` and
 * must move with it.
 */

// ---------------------------------------------------------------------------
// Column defaults — the mirror
// ---------------------------------------------------------------------------

/**
 * `FieldConstraintDefaults.ByColumnInput` (`Domain/Registries/FieldConstraintDefaults.cs`), field
 * for field — `text` bounded at 255 since SR-416 (read 2026-10-18), as `text_short` is. That
 * bound is the server's finding, as a scalar's is: no cell control is cut at it (the owner's ruling
 * of 2026-10-18), only at `MAX_TEXT_VALUE_CHARS`.
 *
 * A cell is bare JSON with no `{t,v}` envelope, so a `currency` column is a plain
 * number and these bounds are not the scalar field types' — which is why the server
 * keeps two tables and so does this. `num` gets no entry on purpose: it is the signed,
 * unbounded escape hatch a column needing negatives is retyped to. `decimals: 0` is
 * what actually makes an `int` column integral, since no value schema validates a cell.
 */
const COLUMN_DEFAULTS: Record<string, IColumnConstraint> = {
  currency: { min: 0 },
  int: { min: 0, decimals: 0 },
  percent: { min: 0, max: 100 },
  text: { max_length: 255 }
};

/**
 * A column's effective constraint: the input's default overridden key by key by whatever
 * the column authored, `signed` applied.
 *
 * `signed: true` clears `min` **unconditionally**, which is the whole reason the flag
 * exists: `"min": null` cannot say "drop the inherited floor" on the wire, because the
 * server's JSON reader cannot tell an absent key from an explicit null. Publish refuses
 * `signed` alongside an authored `min`, so clearing rather than merging cannot lose a
 * bound anybody meant to set.
 */
export function resolveColumnConstraint(column: IGridColumn): IColumnConstraint {
  const base = COLUMN_DEFAULTS[column.input] ?? {};
  const authored = column.constraint ?? {};
  const merged: IColumnConstraint = {
    min: authored.min ?? base.min,
    max: authored.max ?? base.max,
    decimals: authored.decimals ?? base.decimals,
    max_length: authored.max_length ?? base.max_length
  };
  return authored.signed === true ? { ...merged, min: null } : merged;
}

// ---------------------------------------------------------------------------
// Masks
// ---------------------------------------------------------------------------

const DIGIT = '9';
const LETTER = 'A';

function isSlot(character: string): boolean {
  return character === DIGIT || character === LETTER;
}

function fitsSlot(slot: string, character: string): boolean {
  return slot === DIGIT ? character >= '0' && character <= '9' : /[a-z]/i.test(character);
}

/**
 * The mask as an input hint: every slot an underscore, every literal itself.
 * `999-999-9999` reads as `___-___-____`.
 *
 * Not the mask verbatim: `999-999-9999` in a placeholder reads as a phone number that
 * is already there, and a respondent who mistakes a placeholder for a value leaves the
 * field empty.
 */
export function maskPlaceholder(mask: string): string {
  return [...mask].map((character) => (isSlot(character) ? '_' : character)).join('');
}

/**
 * The value re-spelled in the mask's positional form — **or the value untouched**.
 *
 * This is a soft mask, and the softness is the point. survey-api's format grammars are
 * deliberately tolerant of shapes a mask cannot produce ("can this be the thing", not
 * "is this in canonical form"), because paste and staff entry never pass through a mask
 * and legacy stores the same phone number in three shapes. A mask that *filtered* would
 * therefore be stricter than the check it is helping satisfy — `postal_code_na` masks as
 * `99999` and would make every Canadian postal code untypeable, and a `+44` number would
 * lose its country code silently rather than earn the soft finding it is meant to earn.
 *
 * So: when every significant character the respondent has typed fits the slot waiting for
 * it and none overflows the mask, the masked spelling is returned — `2125551234` becomes
 * `212-555-1234` as it is typed, and the separators the respondent typed themselves are
 * absorbed rather than doubled. The first character that does not fit hands the raw value
 * straight back, and the server judges it.
 *
 * A value typed in another shape is handed back too, as typed, from its first keystroke: a `+`
 * (a country code, which no mask spells), or a separator where the mask has none — `1 212…` is
 * a trunk prefix, not an area code. Re-spelled at its tenth digit, `+1 212 555 123` became
 * `121-255-5123`, and the eleventh digit then landed after it.
 *
 * A trailing literal is never emitted: `212` stays `212` rather than becoming `212-`, so
 * deleting the last digit does not leave a separator behind for the caret to fight.
 */
export function applyMask(mask: string, raw: string): string {
  const typed = [...raw].filter((character) => /[0-9a-z]/i.test(character));
  const slots = [...mask].filter(isSlot).length;
  if (typed.length > slots || raw.includes('+')) return raw;
  const maskSeparators = separatorsOf(mask);
  if (separatorsTyped(raw).some((at) => !maskSeparators.has(at))) return raw;

  let masked = '';
  let index = 0;
  for (const slot of mask) {
    if (index >= typed.length) break;
    if (!isSlot(slot)) {
      masked += slot;
      continue;
    }
    const character = typed[index];
    if (!fitsSlot(slot, character)) return raw;
    // A letter slot upper-cases: `us_state` is checked against USPS codes with an
    // ordinal comparison, so `ny` is a violation where `NY` is not — and no letter slot
    // in the registry means anything but a code.
    masked += slot === LETTER ? character.toUpperCase() : character;
    index += 1;
  }
  return masked;
}

/** How many slots come before each of the mask's literals: `999-999-9999` is {3, 6}. */
function separatorsOf(mask: string): Set<number> {
  const at = new Set<number>();
  let slots = 0;
  for (const character of mask) {
    if (isSlot(character)) slots += 1;
    else if (slots > 0) at.add(slots);
  }
  return at;
}

/**
 * How many significant characters come before each separator the respondent typed. One
 * typed ahead of everything (the `(` of `(212)`) stands before no slot and counts for nothing.
 */
function separatorsTyped(raw: string): number[] {
  const at: number[] = [];
  let significant = 0;
  for (const character of raw) {
    if (/[0-9a-z]/i.test(character)) significant += 1;
    else if (significant > 0) at.push(significant);
  }
  return at;
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * What a violated format should read as, keyed by the `messageCode` the constraint
 * carries.
 *
 * The engine ships a message of its own and it is not this: `is not a valid us_phone`
 * is addressed to whoever reads an evaluation. `messageCode` exists so the respondent's
 * copy lives where respondent copy belongs, and so a wording change here is not a
 * contract change there.
 */
const FORMAT_MESSAGES: Record<string, string> = {
  'format.email': 'Enter a valid email address.',
  'format.corporate_email':
    'Enter a full work email address, such as name@yourfirm.com. Personal addresses (Gmail, Yahoo, Outlook and the like) are not accepted.',
  'format.us_phone': 'Enter a 10-digit US phone number.',
  'format.postal_code_na':
    'Enter a US ZIP code (12345 or 12345-6789) or a Canadian postal code (K1A 0B1).',
  'format.us_state': 'Enter the two-letter state code, for example NY.',
  'format.url': 'Enter a valid web address.',
  'format.month_day': 'Enter a month and day as MM/DD, for example 12/31.'
};

/**
 * The copy for a `messageCode`, or null when there is none for it.
 *
 * Null rather than an invented sentence: an unknown code means the server has grown a
 * format this build has never heard of, and its own message — imperfectly addressed but
 * accurate — is a better thing to show than a guess.
 */
export function formatMessage(messageCode: Nullable<string>): Nullable<string> {
  return messageCode ? (FORMAT_MESSAGES[messageCode] ?? null) : null;
}

/**
 * The example a format shows in an empty field when it has no mask to spell.
 *
 * Three of the six formats have no positional form — an address, a domain and a
 * company address are not fixed-width — so before this they were the only constrained
 * fields on the form with nothing in them at all, while a phone box showed `___-___-____`
 * and a zip `_____`. An example carries what a mask cannot: `corporate_email` differs from
 * `email` by a rule about the *domain*, which no placeholder shape could show and which a
 * respondent otherwise meets for the first time as a rejection.
 *
 * Deliberately not a plausible real value. `name@firm.com` reads as an instruction;
 * `j.smith@morganstanley.com` reads as somebody's address, and a placeholder mistaken
 * for a value is a field left empty.
 */
const PLACEHOLDER_BY_FORMAT: Record<string, string> = {
  email: 'name@firm.com',
  corporate_email: 'name@yourfirm.com — not a personal address',
  url: 'www.yourfirm.com'
};

/** `autocomplete` for a format, so the browser can offer what it already knows. */
const AUTOCOMPLETE_BY_FORMAT: Record<string, string> = {
  email: 'email',
  corporate_email: 'email',
  us_phone: 'tel',
  postal_code_na: 'postal-code',
  us_state: 'address-level1',
  url: 'url'
};

// ---------------------------------------------------------------------------
// Control shape
// ---------------------------------------------------------------------------

/** The attributes a constraint decides on an `<input>` / `<textarea>`. */
export interface IControlShape {
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'numeric' | 'decimal';
  /**
   * A hard ceiling on what the control will hold — **not** `constraint.maxLength`.
   *
   * The configured length is a finding by design, "so the respondent never loses typed
   * input", and an HTML `maxLength` is the opposite of that: it truncates a paste in
   * silence. What is capped here is survey-api's 64 KiB write-time payload guard, which
   * is not a finding but an outright refusal of the whole batch — so passing it does not
   * flag one field, it loses every other answer queued beside it.
   */
  maxLength?: number;
}

/**
 * survey-api's `MaxTextValueChars` — the write path refuses a text value longer than
 * this with `value_too_large`, and a batch is all-or-nothing.
 */
export const MAX_TEXT_VALUE_CHARS = 64 * 1024;

/**
 * How to shape the control for a scalar usage, from the constraint the server resolved.
 *
 * Numeric bounds are deliberately not HTML attributes: these controls are `type="text"`
 * with an `inputMode`, so `min`/`max`/`step` would do nothing at all, and switching them
 * to `type="number"` to make them work would cost the caret behaviour the write protocol
 * depends on (a spinner, a locale-dependent decimal separator, and a browser that
 * silently reports an empty value for text it considers unparseable). `decimals` does
 * reach the control, as the keyboard it opens.
 */
/** Undefined as well as null: `ISnapshotUsage.constraint` is optional, because a
 *  survey-api older than SR-122 sends none at all. */
export function controlShape(
  constraint: Nullable<ISnapshotConstraint> | undefined
): IControlShape {
  const shape: IControlShape = { maxLength: MAX_TEXT_VALUE_CHARS };
  if (!constraint) return shape;

  // The mask wins where there is one: a positional form shows the shape exactly, and an
  // example would only be a second, vaguer statement of the same thing.
  if (constraint.mask) shape.placeholder = maskPlaceholder(constraint.mask);
  else if (constraint.format) shape.placeholder = PLACEHOLDER_BY_FORMAT[constraint.format];
  if (constraint.format) shape.autoComplete = AUTOCOMPLETE_BY_FORMAT[constraint.format];
  // `typeof`, not `!== null`: the type says null-or-number, but an older API build that
  // omits the key entirely would make `undefined !== null` true and open a decimal keypad
  // on a field nothing said anything about.
  if (typeof constraint.decimals === 'number') {
    shape.inputMode = constraint.decimals === 0 ? 'numeric' : 'decimal';
  }
  return shape;
}

/**
 * Whether a length counter is worth showing, and what it should read.
 *
 * Only near the ceiling: a counter under every text box is noise, and the one place it
 * earns its space is the narrative answer that is about to be flagged for length. 80% is
 * far enough in that nobody sees it by accident and early enough to still be a warning
 * rather than a post-mortem.
 */
export function lengthNotice(
  value: string,
  constraint: Nullable<ISnapshotConstraint> | undefined
): Nullable<string> {
  const limit = constraint?.maxLength;
  if (!limit || value.length < limit * 0.8) return null;
  return `${value.length} / ${limit} characters`;
}

// ---------------------------------------------------------------------------
// Picked rather than typed — the survey review's R5 and R16 (the plan's D13)
// ---------------------------------------------------------------------------

/**
 * The years a `year` usage is picked from, newest first: the constraint's `max` down to its `min`,
 * both resolved server-side (the latest is the cycle's year). Null unless both bounds are there —
 * an open range stays a box. A list offers only what the bounds allow, which asks for a valid year;
 * it judges nothing: a stored year outside them is still drawn, beside the engine's finding.
 */
export function yearChoices(
  fieldType: FieldTypeCode,
  constraint: Nullable<ISnapshotConstraint> | undefined
): Nullable<string[]> {
  const min = constraint?.min;
  const max = constraint?.max;
  if (fieldType !== 'year' || typeof min !== 'number' || typeof max !== 'number' || max < min) {
    return null;
  }
  return Array.from({ length: max - min + 1 }, (_, index) => String(max - index));
}

/** A `month_day` usage (`MM/DD`) is asked as a month list and a day list, composing the same text. */
export function isMonthDay(constraint: Nullable<ISnapshotConstraint> | undefined): boolean {
  return constraint?.format === 'month_day';
}

/** The month list's codes (`01`–`12`) and words. */
export const MONTHS: readonly { code: string; label: string }[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
].map((label, index) => ({ code: String(index + 1).padStart(2, '0'), label }));

/** The day list's codes, `01`–`31` for every month: which day a month has is the engine's to say. */
export const DAYS: readonly string[] = Array.from({ length: 31 }, (_, index) =>
  String(index + 1).padStart(2, '0')
);

/** A stored `MM/DD` as its two parts, each empty where the text holds none. */
export function monthDayParts(text: string): { month: string; day: string } {
  const [month = '', day = ''] = text.split('/');
  return { month: month.trim(), day: day.trim() };
}
